import React from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import { Form } from '../../bulma'
import SearchTypeahead from './SearchTypeahead'
import { helpers } from '../../../helpers'

/**
* Container for the main site searchbar.
*
* TODO: Add taxon search filters. It's already setup to work, but I think it
* just needs to be played with a little bit more to ensure the UX is solid for it,
* and we also need to figure out if/when we preselect one of the taxons in the
* list. So until we get time to do that, I'm just going to hide it for now.
*
* TODO: Add random search phrases. This isn't necessary for v1, but won't be hard to
* add and it's already setup in Prismic, and data should be passed down to the proper
* place. We just need to add it. This will let marketing go in add add additional
* placeholder phrases to the search bar input field. This will hopefully encourage
* users to search more.
*
* TODO: Add curated suggestions. This is already setup in Prismic, we just need to
* set it up so that these suggestions are displayed when there are no other suggestions
* (other than history). I've tried to set it up in a way that this should be fairly
* easy to add, and should just be related to the setDefaultSuggestions function.
*
* TODO: Currently the cached results are not persisted between pages. This means
* that if a user searches "Book of Mormon", and then on the next page searches
* "Book of Mormon" again, it will have to hit the server twice to fetch these
* results. The biggest gain by us caching these results are that the user won't
* have to wait for the results to come back.
*
* TODO: Combine or remove duplicate results. If a recommendation is present in
* the user's history and both are displayed, then the recommendation in the user's
* history should be displayed and not the regular recommendation. If the
* recommendation has nested/related recommendations, then those nested/related
* recommendations should still be shown below the duplicate recommendation, but
* we should show the history icon instead of the search icon.
*
* TODO: Suggestion word highlighting currently only highlights the first instance
* of a word, so if a word is present multiple times it won't be highlighted (bolded).
* An example of this would be: "The Church of Jesus Christ of Latter-day Saints",
* only the first "of" is bolded. This is an issue with the `Autosuggest` component's
* highlighting helper methods, so we will either have to override it, or roll our
* own.
*
* TODO: There is an issue where on mobile width screens, accidentally double
* clicking will select the first suggestion. This is because while the animation
* hasn't completed, the suggestion is still there and takes the place of the
* input field. So somehow we need to prevent selection of the suggestion until
* the animation is completed.
*
* TODO: Add exit animations, currently the animations of the input, suggestions,
* and background overlay all only work on focus, but on blur they aren't triggered.
* This would just take some time and css wizardry.
*
* TODO: There is a known issue when navigating the typeahead with arrow keys that
* a blank option is the first option displayed. This is because of the placeholder,
* and how the `Autocomplete` component handles navigating with the keyboard. Not
* a super huge issue, but might be worth looking into some day.
*
* TODO: Add event tracking. This will just require a little bit of time trying to
* develop a useful/consistent schema we can track. We already track the keywords
* that people search for, so my initial suggestion would be to only track focus/blur,
* submit, and clear. I would set the category as "Search Bar" or something and
* then then the action would be "Open", "Close", "Clear", "Select Suggestion" or
* "Search", then the event label would be how that action occured, so maybe
* "button click" or "enter" for "search". The tricky part is making sure that
* only one of these events are registered, and being able to distingush how the
* event occured. So it would be useful to see if users tab in/out of the input to
* focus it, or if they are clicking on it. It would be useful to see if users are
* selecting suggestions with a keyboard or mouse, it would also be useful to
* somehow see which type of recommendation they are selecting and what position it is in.
*
* TODO: Possibly add link refs, unless we decide that tracking with event tracking
* is enough.
*
* TODO: Fix the `onSubmit` handler so that `forceSubmit` triggers a real form
* submission and doesn't have to mimick it. This would make it so it is more
* futureproof as we add/modify the params in the form, then we won't have to
* continue to try to mimick the behavior.
*
* TODO: Fix issue with screen readers reading the wrong line. This was only tested
* briefly on Chrome with the Mac VoiceOver, but it seems that when navigating the
* menu with the arrow keys, the VoiceOver was reading the line before the one it
* should be. I tried removing the "placeholder" suggestion type, but that didn't
* resolve the issue. So we need to figure out how the screen reader is being told
* which suggestion is the active one and determine if it's an issue on our end
* or if we need to open a ticket with `react-autosuggest`. This issue is important
* as we try to make our site as accessible as possible.
*
* TODO: Figure out what to do with overflowing content. Currently if a suggestion
* is too long, it just gets cut off. This probably isn't the best way to handle
* this as if there are bread crumbs it might not be known, or if it cuts off
* at a word, there's no indication of more text. Perhaps the best solution would
* be to add an ellipsis to the highlight text and then wrap the breadcrumbs onto
* a second line. We should try to keep the general height of each suggestion the
* same/similar so you'd have to play with the padding, also the hardest part is
* figuring out when to actually break the suggestion into separate parts.
*
* TODO: Consider adding in the "fill in this text but let me keep typing button"
* that a lot of search bars implement. This would only make sense once our backend
* provides a more true typeahead instead of mini search results.
*/
class SearchbarContainer extends React.Component {
  constructor(props) {
    super(props)

    // We will temporarily cache each typeahead request to possibly cut down on
    // the number of requests made.
    this.cache = {}

    const history = localStorage ? JSON.parse(localStorage.getItem('dbcom:search:history')) || [] : []

    this.state = {
      value: new Url().query.keywords || '',
      suggestions: [
        // Placeholder ensures animations fire even when no suggestions are available.
        {
          id: '_placeholder',
          visible: false,
          suggestions: [{}],
        },
        {
          id: 'history',
          visible: true,
          // In an empty state show 5 history records, otherwise show 3.
          suggestions: history.slice(0, 5),
        },
        {
          id: 'taxon',
          visible: true,
          suggestions: [],
        },
        {
          id: 'product',
          visible: true,
          suggestions: [],
        },
      ],
      isSubmitted: false,
      isLoading: false,
      productsLoading: 0,
      taxonsLoading: 0,
    }

    // Bind methods
    this.updateLoadingState = this.updateLoadingState.bind(this)
    this.fetchSuggestions = this.fetchSuggestions.bind(this)
    this.updateSuggestions = this.updateSuggestions.bind(this)
    this.suggestionsFetchRequestedHandler = this.suggestionsFetchRequestedHandler.bind(this)
    this.onInputChangeHandler = this.onInputChangeHandler.bind(this)
    this.onSubmitHandler = this.onSubmitHandler.bind(this)

    // Debounce wrappers for each type of suggestion. This helps cut down on
    // the number of requests made. We declare them this way so that we are
    // always calling the same debounce method for this component which allows
    // the debouncer to work as expected.
    this.fetchHistory = helpers.debounce(value => this.fetchSuggestions('history', value, 3), 200)
    this.fetchProducts = helpers.debounce(value => this.fetchSuggestions('product', value, 5), 200)
    this.fetchTaxons = helpers.debounce(value => this.fetchSuggestions('taxon', value, 5), 200)
  }

  /**
  * Fetch initial results based on the default search query.
  */
  componentDidMount() {
    this.suggestionsFetchRequestedHandler({ value: this.state.value, reason: 'componentDidMount' })
  }

  /**
  * Updates the component's state to indicate the given category is loading or not.
  *
  * @param category {string} category that is being updated.
  * @param isLoading {bool} if the given category is currently loading.
  */
  updateLoadingState(category, isLoading) {
    const key = category === 'taxon' ? 'taxonsLoading' : 'productsLoading'
    const count = this.state[key] || 0

    this.setState({ [key]: isLoading ? count + 1 : count - 1 })
  }

  /**
  * Fetches new suggestions for the given category and matching the given value.
  *
  * @param category {string} Type of suggestions to retrieve.
  * @param value {string} Keyword for suggestions.
  */
  fetchSuggestions(category, value, count = 5) {
    // Update loading state.
    this.updateLoadingState(category, true)
    dbcom.recommendations.autosuggest(
      {
        type: category, keywords: value, strict: true, count,
      },
      (data) => {
        // Update the cache so we don't have to deal with this again.
        const cacheKey = value.trim().toLowerCase()

        if (this.cache[cacheKey]) {
          this.cache[cacheKey][category] = data
        } else {
          this.cache[cacheKey] = { [category]: data }
        }

        // Since this request could be obsolete by the time it gets back from
        // the server, only display these results if they are still the ones
        // the user is waiting for.
        if (value === this.state.value) {
          this.updateSuggestions(category, data)
        }

        // Clear the loading status.
        this.updateLoadingState(category, false)
      },
      (data) => {
        this.updateLoadingState(category, false)
      },
    )
  }

  /**
  * Updates the current suggestions for the given `categoryId`.
  *
  * @param category {string}
  * @param data {array} Updated suggestions for category.
  */
  updateSuggestions(category, data) {
    // Iterate all suggestions, and once the suggestions for the given category
    // are found, then replace those suggestions with the new ones.
    const suggestions = this.state.suggestions
      .map((group) => {
        // Just return the group if this isn't the one we're looking for.
        if (group.id !== category) {
          return group
        }
        // Update the group's suggestions, then return this updated group.
        group.suggestions = data

        return group
      })

    this.setState({ suggestions })
  }

  /**
  * Tries to update the suggestions that are displayed.
  *
  * > Will be called every time you need to recalculate `suggestions`.
  *
  * @param {object} ata returned by Autosuggest component.
  */
  suggestionsFetchRequestedHandler({ value, reason }) {
    // Don't try to load suggestions if there's no value.
    if (!value || !value.length) return

    // Try to update each type of suggestions based on the new value.
    this.state.suggestions
      .map((category) => {
        if (!category.visible) {
          return
        }
        const type = category.id
        // Check the cache for known suggestions.
        const cacheKey = value.trim().toLowerCase()

        if (this.cache[cacheKey]) {
          this.updateSuggestions(type, this.cache[cacheKey][type])
        } else {
          // Fetch new suggestions based on given category.
          switch (type) {
            case 'taxon':
              this.fetchTaxons(value)
              break
            case 'history':
              this.fetchHistory(value)
              break
            default:
              this.fetchProducts(value)
              break
          }
        }
      })
  }

  /**
  * onChange handler for when the search input's value has changed.
  *
  * @param e {event}
  * @param {object} data returned by `Autosuggest` component.
  */
  onInputChangeHandler(e, { newValue, method }) {
    this.setState({
      value: newValue,
    })
  }

  /**
  * Handles form submission and when an autosuggest item is selected.
  *
  * @param value {string} search query
  * @param slug {string} destination for search query.
  * @param forceSubmit {boolean} If the traditional submit should be forced for this instance.
  */
  onSubmitHandler(value = this.state.value, slug, forceSubmit = false) {
    this.setState({ isSubmitted: true })
    dbcom.recommendations.saveSearch(value, slug, () => {
      if (forceSubmit) {
        Turbolinks.visit(slug || `/products?keywords=${value}`)
      }
    })
  }

  render() {
    const {
      value, suggestions, isLoading, productsLoading, taxonsLoading, isSubmitted,
    } = this.state
    const { phrases, taxonFilters } = this.props

    // Decide if there are any suggestions to actually show
    const hasSuggestions = suggestions
      .filter(category => category.visible && category.suggestions && category.suggestions.length)
      .length

    // Filter out all the empty suggestion groups, but leave hidden groups in if there aren't any
    // real suggestions to show so things will render properly.
    const filteredSuggestions = suggestions
      .reduce((categories, category) => {
        if ((hasSuggestions && !category.visible) || !(category.suggestions && category.suggestions.length)) {
          return categories
        }

        return [
          ...categories,
          category,
        ]
      }, [])

    return (
      <Form
        action="/products"
        className="typeahead-form"
        onSubmit={() => {
 this.onSubmitHandler(value)
}}
      >
        <SearchTypeahead
          phrases={phrases}
          taxons={taxonFilters}
          suggestions={filteredSuggestions}
          value={value}
          isLoading={isLoading || Boolean(productsLoading) || Boolean(taxonsLoading)}
          isSubmitted={isSubmitted}
          onChange={this.onInputChangeHandler}
          onSubmit={this.onSubmitHandler}
          onSuggestionsFetchRequested={this.suggestionsFetchRequestedHandler}
          onSuggestionsClearRequested={() => { }}
        />
      </Form>
    )
  }
}

SearchbarContainer.defaultProps = {
  phrases: [],
  taxonFilters: [],
}

SearchbarContainer.propTypes = {
  // Random phrases to display as placeholders for the search input.
  phrases: PropTypes.array,
  // Taxon filters to narrow scope of search query.
  taxonFilters: PropTypes.array,
}

export default SearchbarContainer
